import React, { useMemo } from "react";
import type { WorkSessionProof, SessionGapProof } from "../../../types/core";
import { getBucketColor } from "./bucketColors";

interface SessionProofTimelineProps {
  sessions: WorkSessionProof[];
  gaps?: SessionGapProof[];
  setterName: string;
  timezone: string;
}

function formatTime(iso: string, timezone: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });
}

function formatDate(iso: string, timezone: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
}

export const SessionProofTimeline: React.FC<SessionProofTimelineProps> = ({
  sessions,
  gaps,
  setterName,
  timezone,
}) => {
  // Build interleaved timeline: session, gap, session, gap, ...
  // (useMemo must be called before any early returns)
  const gapMap = useMemo(() => {
    const map = new Map<number, SessionGapProof>();
    if (gaps) {
      gaps.forEach((g) => {
        const matchSession = sessions.find((s) => s.session_start === g.next_session_start);
        if (matchSession) {
          map.set(matchSession.session_number, g);
        }
      });
    }
    return map;
  }, [gaps, sessions]);

  if (sessions.length === 0) {
    return (
      <p className="token-14 text-glass-muted py-4 text-center">No sessions for {setterName}.</p>
    );
  }

  return (
    <div>
      <h4 className="token-14 font-semibold text-glass-secondary mb-3">{setterName}</h4>
      <div className="space-y-1">
        {sessions.map((session) => {
          const gap = gapMap.get(session.session_number);
          return (
            <React.Fragment key={session.session_number}>
              {/* Gap before this session */}
              {gap && (
                <div className="flex items-center gap-2 px-3 py-1">
                  <div className={`w-2 h-2 rounded-full ${getBucketColor(gap.bucket)}`} />
                  <span className="token-11 text-glass-muted">
                    Gap: {gap.gap_mins.toFixed(0)} min
                  </span>
                </div>
              )}
              {/* Session block */}
              <div className="bg-black/[0.03] rounded-lg px-4 py-2.5 flex items-center gap-4">
                <div className="shrink-0 token-11 text-glass-muted">
                  <div>{formatDate(session.session_start, timezone)}</div>
                  <div className="font-medium text-glass-secondary">
                    {formatTime(session.session_start, timezone)} —{" "}
                    {formatTime(session.session_end, timezone)}
                  </div>
                </div>
                <div className="flex-1 flex gap-4 token-11">
                  <span className="text-glass-secondary">
                    <span className="font-medium text-glass">
                      {session.duration_mins.toFixed(0)}
                    </span>{" "}
                    min
                  </span>
                  <span className="text-glass-secondary">
                    <span className="font-medium text-glass">{session.conversations_handled}</span>{" "}
                    convos
                  </span>
                  <span className="text-glass-secondary">
                    <span className="font-medium text-glass">{session.messages_sent}</span> msgs
                  </span>
                </div>
                <span className="token-11 text-glass-muted shrink-0">
                  #{session.session_number}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <p className="token-11 text-glass-muted mt-2">{sessions.length} sessions total</p>
    </div>
  );
};
